﻿import { normalizeSearchText } from "@js/core/utils/search";
import { ensureJQuery, ensureSelect2 } from "@js/core/loaders/vendors";
import { t } from "@js/core/utils/i18n";

function getActivePanel() {
    return document.querySelector(".tds-panel.is-active");
}

function updateClearVisibility(searchInput, clearSearchButton) {
    if (!searchInput || !clearSearchButton) return;
    clearSearchButton.classList.toggle("is-visible", searchInput.value.trim().length > 0);
}

function updateEmptyState(panel, visibleDocumentCount) {
    if (!panel) return;

    const items = panel.querySelectorAll(".tds-item");
    const emptyState = panel.querySelector(".tds-empty-state");
    if (!emptyState) return;

    let visibleCount = 0;

    items.forEach((item) => {
        if (item.style.display !== "none") {
            visibleCount++;
        }
    });

    emptyState.classList.toggle("is-visible", visibleCount === 0);

    if (visibleDocumentCount) {
        visibleDocumentCount.textContent = String(visibleCount);
    }
}

function applyFilters(searchInput, brandFilter, visibleDocumentCount) {
    const panel = getActivePanel();
    if (!panel) return;

    const items = panel.querySelectorAll(".tds-item");
    const term = normalizeSearchText(searchInput?.value || "");
    const brand = brandFilter?.value || "";

    items.forEach((item) => {
        const haystack = normalizeSearchText(item.dataset.search || "");
        const itemBrand = item.dataset.brand || "";

        const matchesTerm =
            !term ||
            haystack.includes(term) ||
            haystack.replace(/\s+/g, "").includes(term.replace(/\s+/g, ""));

        const matchesBrand = !brand || itemBrand === brand;

        item.style.display = matchesTerm && matchesBrand ? "" : "none";
    });

    updateEmptyState(panel, visibleDocumentCount);
}

function activateCategory(category, categoryTabs, panels, searchInput, brandFilter, visibleDocumentCount) {
    categoryTabs.forEach((tab) => {
        tab.classList.toggle("is-active", tab.dataset.categoryTarget === category);
    });

    const activeTab = Array.from(categoryTabs).find((tab) => tab.dataset.categoryTarget === category);
    activeTab?.scrollIntoView({ behavior: "smooth", block: "nearest", inline: "center" });

    panels.forEach((panel) => {
        panel.classList.toggle("is-active", panel.dataset.categoryPanel === category);
    });

    applyFilters(searchInput, brandFilter, visibleDocumentCount);
}

async function initBrandFilter(brandFilter, applyFiltersCallback) {
    if (!brandFilter) return;

    await ensureJQuery();
    await ensureSelect2();

    const $ = window.jQuery;
    const $brand = $(brandFilter);

    $brand.select2({
        width: "220px",
        placeholder: t("Tds.BrandPlaceholder"),
        allowClear: true,
        minimumResultsForSearch: 8,
        dropdownAutoWidth: true
    });

    brandFilter.classList.remove("is-select2-pending");
    brandFilter.classList.add("is-select2-ready");

    $brand.on("change", function () {
        applyFiltersCallback();
    });
}

export async function initTdsPage() {
    const categoryTabs = document.querySelectorAll(".category-tab");
    const panels = document.querySelectorAll(".tds-panel");
    const documentCards = document.querySelectorAll(".tds-item[data-detail-url]");
    const searchInput = document.getElementById("tdsSearchInput");
    const clearSearchButton = document.getElementById("clearTdsSearch");
    const brandFilter = document.getElementById("brandFilter");
    const visibleDocumentCount = document.getElementById("visibleDocumentCount");
    const resetButtons = document.querySelectorAll('[data-clear-tds-search="true"]');

    if (!categoryTabs.length && !panels.length) return;

    documentCards.forEach((card) => {
        const openDetail = () => {
            const url = card.dataset.detailUrl;
            if (url) window.location.assign(url);
        };

        card.addEventListener("click", (event) => {
            if (event.target.closest("a, button, input, select, label")) return;
            openDetail();
        });

        card.addEventListener("keydown", (event) => {
            if (event.key !== "Enter" && event.key !== " ") return;
            event.preventDefault();
            openDetail();
        });
    });

    const runFilters = () => applyFilters(searchInput, brandFilter, visibleDocumentCount);

    categoryTabs.forEach((tab) => {
        tab.addEventListener("click", function () {
            activateCategory(
                this.dataset.categoryTarget,
                categoryTabs,
                panels,
                searchInput,
                brandFilter,
                visibleDocumentCount
            );
        });
    });

    if (searchInput) {
        searchInput.addEventListener("input", function () {
            updateClearVisibility(searchInput, clearSearchButton);
            runFilters();
        });
    }

    if (clearSearchButton) {
        clearSearchButton.addEventListener("click", function () {
            if (!searchInput) return;

            searchInput.value = "";
            updateClearVisibility(searchInput, clearSearchButton);
            runFilters();
            searchInput.focus();
        });
    }

    resetButtons.forEach((btn) => {
        btn.addEventListener("click", function () {
            if (searchInput) searchInput.value = "";

            if (brandFilter) {
                brandFilter.value = "";

                if (window.jQuery?.fn?.select2) {
                    window.jQuery(brandFilter).val("").trigger("change");
                }
            }

            updateClearVisibility(searchInput, clearSearchButton);
            runFilters();
            searchInput?.focus();
        });
    });

    const defaultCategory = document.body?.dataset?.tdsDefaultCategory || "";
    if (defaultCategory) {
        activateCategory(
            defaultCategory,
            categoryTabs,
            panels,
            searchInput,
            brandFilter,
            visibleDocumentCount
        );
    }

    await initBrandFilter(brandFilter, runFilters);

    updateClearVisibility(searchInput, clearSearchButton);

    const activePanel = getActivePanel();
    if (activePanel) {
        updateEmptyState(activePanel, visibleDocumentCount);
    }
}

export function initTdsDetailPage() {
    document.querySelectorAll("[data-copy-url]").forEach((button) => {
        button.addEventListener("click", async () => {
            const url = button.dataset.copyUrl || "";
            const label = button.querySelector("[data-copy-label]");
            const originalLabel = label?.textContent || "";
            const successMessage = button.dataset.copySuccess || originalLabel;
            const errorMessage = button.dataset.copyError || originalLabel;

            try {
                await navigator.clipboard.writeText(url);
                button.classList.add("is-copied");
                if (label) label.textContent = successMessage;
            } catch {
                const fallback = document.createElement("textarea");
                fallback.value = url;
                fallback.style.position = "fixed";
                fallback.style.left = "-9999px";
                document.body.appendChild(fallback);
                fallback.select();

                const copied = document.execCommand("copy");
                fallback.remove();

                if (copied) {
                    button.classList.add("is-copied");
                    if (label) label.textContent = successMessage;
                } else if (label) {
                    label.textContent = errorMessage;
                }
            }

            window.setTimeout(() => {
                button.classList.remove("is-copied");
                if (label) label.textContent = originalLabel;
            }, 2600);
        });
    });

    const mobileActions = document.querySelector("[data-tds-mobile-actions]");
    if (!mobileActions) return;

    const trigger = mobileActions.querySelector("[data-tds-mobile-actions-trigger]");
    const panel = mobileActions.querySelector(".tds-mobile-actions__panel");
    const closeButtons = mobileActions.querySelectorAll("[data-tds-mobile-actions-close]");

    const setMobileActionsOpen = (isOpen) => {
        mobileActions.classList.toggle("is-open", isOpen);
        trigger?.setAttribute("aria-expanded", String(isOpen));
        panel?.setAttribute("aria-hidden", String(!isOpen));
    };

    trigger?.addEventListener("click", () => {
        setMobileActionsOpen(!mobileActions.classList.contains("is-open"));
    });

    closeButtons.forEach((button) => {
        button.addEventListener("click", () => setMobileActionsOpen(false));
    });

    mobileActions.querySelectorAll("a").forEach((link) => {
        link.addEventListener("click", () => setMobileActionsOpen(false));
    });

    document.addEventListener("keydown", (event) => {
        if (event.key === "Escape") setMobileActionsOpen(false);
    });
}
